import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ArrowsClockwise } from '@phosphor-icons/react';
import { toast } from 'sonner';

// Convert UI interval → number of months. Mirrors backend `_months_for_interval`.
const intervalMonths = (interval, customMonths) => {
  const m = { monthly: 1, quarterly: 3, half_yearly: 6, yearly: 12 };
  if (interval === 'custom') return parseInt(customMonths) || null;
  return m[interval] || null;
};

const intervalLabel = (interval, customMonths) => {
  const m = { monthly: 'Monthly', quarterly: 'Quarterly', half_yearly: 'Half-Yearly', yearly: 'Yearly' };
  if (interval === 'custom') return `Custom · ${customMonths || '?'} month(s)`;
  return m[interval] || interval;
};

/**
 * RecurringDatesDialog
 *
 * Opens immediately after the operator picks a recurring service on an
 * invoice row. The operator confirms (or back-dates) the **From** date; the
 * **To** date is auto-computed from the service's interval EXCEPT when the
 * interval is `custom` — in which case the operator can freely override it
 * (useful for one-off odd-length subscriptions e.g. 14 months, 22 days).
 *
 * A free-form **Notes / Remarks** textarea is also provided so the operator
 * can record subscription-specific details (insurance policy number,
 * vehicle number, portal ID, etc.). This flows through to the Recurring
 * Subscriptions page where it renders under a dedicated Remarks column and
 * can be edited in place.
 *
 * Props:
 *   open / onOpenChange — controlled visibility
 *   service             — the picked service { name, recurring_interval, recurring_interval_months }
 *   initialFromDate     — default ISO "YYYY-MM-DD" (usually the invoice date)
 *   initialToDate       — optional ISO "YYYY-MM-DD" pre-fill for the To date
 *                         (used when re-opening for an already-set custom interval)
 *   initialNotes        — optional pre-fill for the Remarks field
 *   onConfirm({ from, to, notes }) — fires when staff hits Save
 */
export default function RecurringDatesDialog({
  open,
  onOpenChange,
  service,
  initialFromDate,
  initialToDate,
  initialNotes,
  onConfirm,
}) {
  const todayIso = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(initialFromDate || todayIso);
  const [toOverride, setToOverride] = useState(initialToDate || '');
  const [notes, setNotes] = useState(initialNotes || '');

  const months = intervalMonths(service?.recurring_interval, service?.recurring_interval_months);
  const isCustom = service?.recurring_interval === 'custom';

  // Reset whenever the dialog re-opens (different row / service).
  useEffect(() => {
    if (open) {
      setFrom(initialFromDate || todayIso);
      setToOverride(initialToDate || '');
      setNotes(initialNotes || '');
    }
  }, [open, initialFromDate, initialToDate, initialNotes, todayIso]);

  // Compute the EXPIRY (To) date — subscription valid up to ONE DAY BEFORE
  // the next renewal anniversary. Example: monthly start 27/5/2026 →
  // anniversary 27/6/2026, expiry shown to the operator = 26/6/2026.
  // We clamp on the anniversary date first (to handle short months like
  // 31 Jan → 28 Feb) and then subtract a day.
  const computeTo = (fromStr, m) => {
    if (!fromStr || !m) return '';
    const [y, mo, d] = fromStr.split('-').map(Number);
    const start = new Date(y, mo - 1, d);
    const anniversary = new Date(start.getFullYear(), start.getMonth() + m, 1);
    const lastDayOfTarget = new Date(anniversary.getFullYear(), anniversary.getMonth() + 1, 0).getDate();
    anniversary.setDate(Math.min(d, lastDayOfTarget));
    // Expiry = anniversary − 1 day.
    anniversary.setDate(anniversary.getDate() - 1);
    const yy = anniversary.getFullYear();
    const mm = String(anniversary.getMonth() + 1).padStart(2, '0');
    const dd = String(anniversary.getDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
  };

  // For Custom intervals the operator picks To directly; for every other
  // preset we always derive from the interval so the operator can't get out
  // of sync with the service definition.
  const computedTo = computeTo(from, months);
  const to = isCustom ? (toOverride || computedTo) : computedTo;

  const handleSave = () => {
    if (!from) { toast.error('Pick a from date'); return; }
    if (!to) { toast.error('Pick a To date'); return; }
    if (isCustom && toOverride && toOverride < from) {
      toast.error('To date must be on or after From date');
      return;
    }
    onConfirm?.({ from, to, notes: (notes || '').trim() });
    onOpenChange?.(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl border-[#E4E4E7] max-w-md" data-testid="recurring-dates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <ArrowsClockwise size={18} weight="duotone" className="text-[#0284C7]" />
            Recurring Period
          </DialogTitle>
          <DialogDescription className="text-xs text-[#52525B]">
            {service?.name ? <><strong>{service.name}</strong> · </> : ''}
            {intervalLabel(service?.recurring_interval, service?.recurring_interval_months)}
            {isCustom
              ? ' · pick your own To date'
              : (months ? ` · auto-fills To date after ${months} month${months === 1 ? '' : 's'}` : '')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">From Date</Label>
              <Input
                type="date"
                value={from}
                onChange={e => setFrom(e.target.value)}
                className="rounded-lg mt-1 h-9 font-mono"
                data-testid="rrd-from-date"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">
                To Date{' '}
                {isCustom
                  ? <span className="text-[9px] text-[#0284C7]">(editable)</span>
                  : <span className="text-[9px] text-[#A1A1AA]">(auto)</span>}
              </Label>
              <Input
                type="date"
                value={to}
                min={from || undefined}
                onChange={e => setToOverride(e.target.value)}
                readOnly={!isCustom}
                className={`rounded-lg mt-1 h-9 font-mono ${isCustom ? '' : 'bg-[#F4F4F5] cursor-not-allowed'}`}
                data-testid="rrd-to-date"
              />
            </div>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.12em] font-semibold text-[#52525B]">
              Remarks <span className="text-[9px] text-[#A1A1AA]">(policy #, vehicle #, portal ID…)</span>
            </Label>
            <Textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              placeholder="e.g. Policy #LIC-8871234 · Vehicle KL-07 AZ 1234"
              rows={2}
              className="rounded-lg mt-1 text-sm"
              data-testid="rrd-notes"
            />
          </div>
          {!months && (
            <p className="text-[11px] text-[#EF4444] italic">
              This service is marked recurring but has no interval set. Open Services and fix it before billing.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} className="rounded-lg" data-testid="rrd-cancel-btn">Cancel</Button>
          <Button onClick={handleSave} disabled={!months || !from} className="rounded-lg bg-[#0284C7] text-white hover:bg-[#0369A1] hover:text-white disabled:opacity-50" data-testid="rrd-save-btn">
            Save Period
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
